import { useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import {
  Home,
  Building2,
  Users,
  DollarSign,
  MoreHorizontal,
  LogOut,
  Menu,
  X,
  Search,
  FileText,
  Wrench,
  Receipt,
  BarChart3,
  Settings,
  HelpCircle,
  Briefcase,
  TrendingUp,
  UserCircle,
  ChevronDown
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "./ui/sheet";

const Layout = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Main navigation - max 5 items
  const mainNav = [
    { to: "/dashboard", icon: Home, label: "Home" },
    { to: "/properties", icon: Building2, label: "Properties" },
    { to: "/tenants", icon: Users, label: "Tenants" },
    { to: "/rent-payments", icon: DollarSign, label: "Payments" },
    { to: "/more", icon: MoreHorizontal, label: "More", isMore: true },
  ];

  // Items under "More"
  const moreItems = [
    { to: "/leases", icon: FileText, label: "Leases" },
    { to: "/invoices", icon: Receipt, label: "Invoices" },
    { to: "/expenses", icon: Receipt, label: "Expenses" },
    { to: "/work-orders", icon: Wrench, label: "Maintenance" },
    { to: "/clients", icon: Briefcase, label: "Clients & LLCs" },
    { to: "/reports", icon: BarChart3, label: "Reports" },
    { to: "/portfolio", icon: TrendingUp, label: "Portfolio Value" },
    { to: "/help", icon: HelpCircle, label: "Help" },
  ];

  if (isAdmin) {
    moreItems.push({ to: "/users", icon: Settings, label: "User Management" });
  }

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Navigate to properties with search
      navigate(`/properties?search=${encodeURIComponent(searchQuery)}`);
      setSearchQuery("");
      setSearchOpen(false);
    }
  };

  const isMoreActive = moreItems.some(item => location.pathname === item.to || location.pathname.startsWith(item.to + '/'));

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Top Header - Desktop */}
      <header className="hidden lg:flex sticky top-0 z-30 bg-white border-b border-slate-200 px-6 py-3 items-center justify-between">
        <div className="flex items-center gap-8">
          <img src="/logo.png" alt="AV Realty" className="h-12 w-auto" />
          
          {/* Desktop Nav */}
          <nav className="flex items-center gap-1">
            {mainNav.map((item) => (
              item.isMore ? (
                <DropdownMenu key="more" open={moreMenuOpen} onOpenChange={setMoreMenuOpen}>
                  <DropdownMenuTrigger asChild>
                    <button
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        isMoreActive 
                          ? "bg-green-50 text-green-700" 
                          : "text-slate-600 hover:bg-slate-100"
                      }`}
                    >
                      <item.icon className="w-5 h-5" />
                      <span>{item.label}</span>
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    {moreItems.map((subItem) => (
                      <DropdownMenuItem
                        key={subItem.to}
                        onClick={() => { navigate(subItem.to); setMoreMenuOpen(false); }}
                        className={location.pathname === subItem.to ? "bg-green-50 text-green-700" : ""}
                      >
                        <subItem.icon className="w-4 h-4 mr-3" />
                        {subItem.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive 
                        ? "bg-green-50 text-green-700" 
                        : "text-slate-600 hover:bg-slate-100"
                    }`
                  }
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </NavLink>
              )
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-4">
          {/* Search */}
          <form onSubmit={handleSearch} className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              type="search"
              placeholder="Search properties, tenants..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-64 pl-10 h-10"
            />
          </form>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-100 transition-colors">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <UserCircle className="w-5 h-5 text-green-700" />
                </div>
                <span className="text-sm font-medium text-slate-700">{user?.name?.split(' ')[0]}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div>
                  <p className="font-medium">{user?.name}</p>
                  <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                <LogOut className="w-4 h-4 mr-2" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Mobile Header */}
      <header className="lg:hidden sticky top-0 z-30 bg-white border-b border-slate-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <img src="/logo.png" alt="AV Realty" className="h-10 w-auto" />
          
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSearchOpen(!searchOpen)}
            >
              <Search className="w-5 h-5" />
            </Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <UserCircle className="w-5 h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>{user?.name}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        
        {/* Mobile Search Bar */}
        {searchOpen && (
          <form onSubmit={handleSearch} className="mt-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="search"
                placeholder="Search properties, tenants..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                autoFocus
              />
            </div>
          </form>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-1 p-4 lg:p-6 pb-24 lg:pb-6">
        <Outlet />
      </main>

      {/* Mobile Bottom Tab Bar */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-2 py-2 z-30">
        <div className="flex items-center justify-around">
          {mainNav.map((item) => (
            item.isMore ? (
              <Sheet key="more-sheet" open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <button
                    className={`flex flex-col items-center gap-1 px-3 py-2 rounded-lg min-w-[64px] ${
                      isMoreActive ? "text-green-700" : "text-slate-500"
                    }`}
                  >
                    <item.icon className="w-6 h-6" />
                    <span className="text-xs font-medium">{item.label}</span>
                  </button>
                </SheetTrigger>
                <SheetContent side="bottom" className="h-[70vh] rounded-t-2xl">
                  <SheetHeader>
                    <SheetTitle>More Options</SheetTitle>
                  </SheetHeader>
                  <div className="grid grid-cols-3 gap-4 mt-6">
                    {moreItems.map((subItem) => (
                      <button
                        key={subItem.to}
                        onClick={() => { navigate(subItem.to); setMobileMenuOpen(false); }}
                        className={`flex flex-col items-center gap-2 p-4 rounded-xl transition-colors ${
                          location.pathname === subItem.to 
                            ? "bg-green-50 text-green-700" 
                            : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                        }`}
                      >
                        <subItem.icon className="w-6 h-6" />
                        <span className="text-sm font-medium text-center">{subItem.label}</span>
                      </button>
                    ))}
                  </div>
                </SheetContent>
              </Sheet>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `flex flex-col items-center gap-1 px-3 py-2 rounded-lg min-w-[64px] ${
                    isActive ? "text-green-700" : "text-slate-500"
                  }`
                }
              >
                <item.icon className="w-6 h-6" />
                <span className="text-xs font-medium">{item.label}</span>
              </NavLink>
            )
          ))}
        </div>
      </nav>
    </div>
  );
};

export default Layout;
